import {
  TOP_TIER,
  BOTTOM_TIER,
  promoteCountForGroup,
  relegateCountForGroup,
  tierName,
  type Movement
} from '@h2h/shared';
import { ArrowRight, ChevronRight } from '../../../components/ui/icons';
import { LiveProvisionalBanner } from '../../../components/standings/DivisionStrip';
import { cap, ordinalSuffix } from '../../../lib/format';
import { movementVerdictCopy } from '../../../lib/divisionExplain';
import { cn } from '../../../lib/cn';
import { DivisionRows } from '../division-panel/DivisionRows';
import type { Standing } from '../division-panel/types';

/**
 * Compact division snapshot for the Compete tab — rank, round score, promo race,
 * and a small window of rivals around you. Full history lives on the Standings tab.
 */
export function CompeteLadderSnap({
  isCycling,
  tierIndex,
  rankInGroup,
  divisionSize,
  roundScore,
  movement,
  live,
  liveProgress,
  standings,
  uid,
  onOpenStandings
}: {
  isCycling: boolean;
  tierIndex: number | null;
  rankInGroup: number | null;
  divisionSize: number;
  roundScore: number;
  movement: Movement | null;
  live: boolean;
  liveProgress?: { settled: number; total: number } | null;
  standings: Standing[];
  uid: string | null;
  onOpenStandings: () => void;
}) {
  const tier = tierIndex ?? BOTTOM_TIER;
  const tierLabel = cap(tierName(tier));
  const topTier = tier >= TOP_TIER;
  const bottomTier = tier <= BOTTOM_TIER;
  const size = Math.max(divisionSize, standings.length);
  const promote = promoteCountForGroup(size);
  const relegate = bottomTier ? 0 : relegateCountForGroup(size);
  const hasLadder = !topTier && promote > 0 && promote < size;
  const ranked = rankInGroup != null;
  const inPromo = ranked && rankInGroup <= promote;
  const inDrop = ranked && relegate > 0 && rankInGroup > size - relegate;
  const populated = standings.length > 1;
  const provisional =
    live && !!liveProgress && liveProgress.total > 0 && liveProgress.settled < liveProgress.total;

  let statusLabel: string;
  let statusGood = false;
  if (!ranked) {
    statusLabel = isCycling ? 'Duels score here once the stage settles' : 'Card points land as matches finish';
  } else if (inPromo) {
    statusLabel = topTier ? 'Holding an elite spot' : 'In the promotion places';
    statusGood = true;
  } else if (inDrop) {
    const gap = rankInGroup - (size - relegate);
    statusLabel = `Drop zone · ${gap} ${gap === 1 ? 'place' : 'places'} below safety`;
  } else {
    const gap = rankInGroup - promote;
    statusLabel = `${gap} ${gap === 1 ? 'spot' : 'spots'} from promotion`;
  }

  const filled = inPromo && ranked ? rankInGroup : 0;

  return (
    <div className="mt-5 md:mt-0">
      <div className="mb-2 flex items-end justify-between gap-3 px-0.5">
        <div>
          <div className="text-[13px] font-extrabold tracking-snug text-ink">Your division</div>
          <p className="mt-0.5 text-[11px] font-medium leading-[1.4] text-ink3">
            {live ? 'Provisional until every card settles' : 'Top rivals in your group this round'}
          </p>
        </div>
        <button
          type="button"
          onClick={onOpenStandings}
          className="tap shrink-0 text-[11px] font-bold text-accent-ink"
        >
          Full board
        </button>
      </div>

      <button
        type="button"
        onClick={onOpenStandings}
        className="tap relative w-full overflow-hidden rounded-[20px] bg-[linear-gradient(135deg,#15161D,#0B0B10)] text-left transition-transform active:scale-[0.995]"
      >
        <div className="pointer-events-none absolute -right-[30px] -top-10 h-[140px] w-[140px] rounded-full bg-[radial-gradient(circle,rgba(36,197,106,0.18),transparent_70%)]" />
        <div className="relative px-[17px] pb-4 pt-[14px]">
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              {live && ranked && <span className="h-1.5 w-1.5 animate-h2h-pulse rounded-full bg-accent" />}
              <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-white/55">
                {ranked ? 'Round rank' : 'Division'}
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="rounded-md bg-white/[0.08] px-2 py-0.5 text-[10px] font-bold text-white/65">{tierLabel}</span>
              <ChevronRight width={14} height={14} strokeWidth={2.4} className="text-white/40" />
            </div>
          </div>

          {provisional && (
            <LiveProvisionalBanner settled={liveProgress!.settled} total={liveProgress!.total} className="relative mb-3" />
          )}

          <div className="relative flex items-center gap-[14px]">
            {ranked ? (
              <div className="flex items-baseline gap-[3px]">
                <span className="num text-[34px] font-extrabold leading-[0.9] tracking-[-0.04em] text-white">{rankInGroup}</span>
                <span className="text-[15px] font-bold text-white/50">{ordinalSuffix(rankInGroup)}</span>
              </div>
            ) : (
              <div className="text-[28px] font-extrabold leading-none tracking-[-0.03em] text-white/35">—</div>
            )}
            <div className="flex-1">
              <div className="text-[13px] font-bold text-white/85">
                {ranked ? `of ${size} in your group` : 'Rank pending'}
              </div>
              {movement === 'PROMOTED' && ranked && (
                <div className="mt-0.5 text-[12px] font-bold text-accent">
                  {movementVerdictCopy({ rank: rankInGroup, divisionSize: size, movement: 'PROMOTED' }).why}
                </div>
              )}
              {movement === 'RELEGATED' && ranked && (
                <div className="mt-0.5 text-[12px] font-bold text-danger">
                  {movementVerdictCopy({ rank: rankInGroup, divisionSize: size, movement: 'RELEGATED' }).why}
                </div>
              )}
              {!movement && ranked && (
                <div className="mt-0.5 text-[12px] font-semibold text-white/55">{statusLabel}</div>
              )}
              {!ranked && (
                <div className="mt-0.5 text-[12px] font-semibold text-white/55">{statusLabel}</div>
              )}
            </div>
            <div className="text-right">
              <div className="num text-[22px] font-extrabold tracking-tight text-white">{roundScore.toLocaleString()}</div>
              <div className="text-[10px] font-semibold text-[#9A9AA4]">round pts</div>
            </div>
          </div>

          {hasLadder && ranked && (
            <div className="relative mt-[15px]">
              <div className="mb-[7px] flex items-center justify-between">
                <span className={cn('text-[11px] font-bold', statusGood ? 'text-accent' : inDrop ? 'text-danger' : 'text-white/55')}>
                  {statusLabel}
                </span>
                <span className="text-[11px] font-semibold text-white/50">Top {promote} go up</span>
              </div>
              <div className="flex gap-[3px]">
                {Array.from({ length: promote }).map((_, i) => (
                  <div
                    key={i}
                    className={cn('h-1.5 flex-1 rounded-[3px]', i < filled ? 'bg-accent' : 'bg-white/[0.14]')}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </button>

      {populated && (
        <div className="mt-2 overflow-hidden rounded-[18px] border border-black/7 bg-white">
          <DivisionRows
            standings={standings}
            uid={uid}
            promote={promote}
            divisionSize={size}
          />
        </div>
      )}

      <button
        type="button"
        onClick={onOpenStandings}
        className="tap mt-3 flex w-full items-center justify-center gap-2 rounded-[14px] border border-black/[0.06] bg-fill/50 py-3 text-[12px] font-bold text-ink2"
      >
        Season history &amp; past rounds
        <ArrowRight width={14} height={14} strokeWidth={2.4} />
      </button>
    </div>
  );
}
